import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import RichCourseContent from "@/components/RichCourseContent";
import {
  ArrowLeft, ChevronRight, ChevronLeft, CheckCircle2, Youtube,
  ListChecks, Award, PlayCircle, RotateCcw, Sparkles, BookOpen,
} from "lucide-react";

function ytEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1` : null;
}

export default function CoursePlayer() {
  const { slug, moduleId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/courses/${slug}`).then(({ data }) => {
      setCourse(data);
      if (!data.enrollment) {
        toast.error("Você precisa se matricular no curso primeiro.");
        navigate(`/cursos/${slug}`);
      }
    }).catch(() => navigate("/cursos"));
  };
  useEffect(() => { window.scrollTo(0, 0); setAnswers({}); setResult(null); load(); }, [slug, moduleId]);

  const currentIdx = useMemo(() => (course?.modules || []).findIndex((m) => m.id === moduleId), [course, moduleId]);
  const current = course?.modules?.[currentIdx];
  const prev = currentIdx > 0 ? course?.modules[currentIdx - 1] : null;
  const next = currentIdx >= 0 && currentIdx < (course?.modules?.length || 0) - 1 ? course.modules[currentIdx + 1] : null;
  const modProgress = course?.enrollment?.modules_progress?.[moduleId];
  const alreadyPassed = !!modProgress?.passed_at;

  if (!course || !current) {
    return <div className="container-cuidar py-14 animate-pulse space-y-3"><div className="h-8 w-72 bg-[hsl(var(--muted))] rounded" /><div className="h-96 bg-[hsl(var(--muted))] rounded-2xl mt-8" /></div>;
  }

  const embed = ytEmbed(current.video_url);
  const questions = current.quiz?.questions || [];
  const allAnswered = questions.length > 0 && questions.every((_, i) => typeof answers[i] === "number");

  const submitQuiz = async () => {
    if (!allAnswered) { toast.error("Responda todas as perguntas."); return; }
    setSubmitting(true);
    try {
      const arr = questions.map((_, i) => answers[i] ?? -1);
      const { data } = await api.post(`/courses/${slug}/modules/${moduleId}/quiz`, { answers: arr });
      setResult(data);
      if (data.passed) {
        toast.success(`Aprovado! ${data.score}%`);
        if (data.course_completed) {
          setTimeout(() => toast.success("Curso concluído! Confira seu certificado.", { duration: 6000 }), 500);
        }
      } else {
        toast.warning(`Você fez ${data.score}%. Precisa de ${data.pass_score}% para passar.`);
      }
      load();
    } catch (_) { toast.error("Falha ao enviar quiz."); }
    finally { setSubmitting(false); }
  };

  const retake = () => { setAnswers({}); setResult(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40">
        <div className="container-cuidar py-8">
          <button onClick={() => navigate(`/cursos/${slug}`)} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="player-back-btn">
            <ArrowLeft className="h-4 w-4" /> {course.title}
          </button>
          <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))]">
                Módulo {current.order} de {course.modules.length}
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-black mt-1 leading-tight" data-testid="module-title">{current.title}</h1>
            </div>
            <div className="w-52">
              <div className="text-xs flex justify-between mb-1"><span>Progresso</span><span className="font-semibold">{course.enrollment.progress_percent}%</span></div>
              <Progress value={course.enrollment.progress_percent} className="h-2" />
            </div>
          </div>
        </div>
      </section>

      <section className="container-cuidar py-10 grid gap-8 lg:grid-cols-[1fr_260px]">
        <div className="space-y-8">
          {embed ? (
            <div className="surface p-2 overflow-hidden">
              <div className="aspect-video rounded-2xl overflow-hidden bg-black">
                <iframe src={embed} title={current.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="w-full h-full border-0" data-testid="module-video-iframe" />
              </div>
            </div>
          ) : current.video_url ? (
            <div className="surface p-4 text-sm flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
              <Youtube className="h-4 w-4" /> Vídeo desta aula: <a href={current.video_url} target="_blank" rel="noreferrer" className="underline">abrir no YouTube</a>
            </div>
          ) : null}

          {current.content && (
            <article className="surface p-6 md:p-10" data-testid="module-content">
              {current.cover_image_url && (
                <div className="mb-8 -mx-6 -mt-6 md:-mx-10 md:-mt-10 relative overflow-hidden rounded-t-2xl">
                  <div className="aspect-[21/9] w-full">
                    <img src={current.cover_image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-6 md:left-10 flex items-center gap-2 text-white">
                    <BookOpen className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-widest drop-shadow">Conteúdo da aula</span>
                  </div>
                </div>
              )}
              <RichCourseContent markdown={current.content} />
            </article>
          )}

          {(current.materials || []).length > 0 && (
            <div className="surface p-6">
              <div className="font-heading font-bold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[hsl(var(--secondary))]" /> Materiais para baixar</div>
              <ul className="mt-3 space-y-2">
                {current.materials.map((mat, i) => (
                  <li key={i}>
                    <a href={`/api/files/${mat.path}`} target="_blank" rel="noreferrer" className="text-sm text-[hsl(var(--primary))] hover:underline">
                      {mat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {questions.length > 0 && (
            <div className="surface p-6 md:p-8" data-testid="module-quiz">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">
                <ListChecks className="h-3.5 w-3.5" /> Quiz do módulo
              </div>
              <div className="mt-2 font-heading text-xl font-black">Verifique seu aprendizado</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                Acerte pelo menos {current.quiz.pass_score}% para concluir este módulo.
                {alreadyPassed && <span className="ml-2 inline-flex items-center gap-1 text-[hsl(var(--primary))] font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Já aprovado</span>}
              </p>

              <ol className="mt-6 space-y-6">
                {questions.map((q, qi) => {
                  const chosen = answers[qi];
                  return (
                    <li key={q.id || qi} className="border-t border-[hsl(var(--border))] pt-5 first:border-t-0 first:pt-0" data-testid={`quiz-question-${qi}`}>
                      <div className="font-semibold">{qi + 1}. {q.prompt}</div>
                      <div className="mt-3 grid gap-2">
                        {q.options.map((opt, oi) => {
                          const active = chosen === oi;
                          return (
                            <button key={oi} type="button" disabled={!!result}
                              onClick={() => setAnswers((p) => ({ ...p, [qi]: oi }))}
                              className={`text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
                                active
                                  ? "bg-[hsl(var(--primary))]/8 border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
                                  : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/40"
                              } ${result ? "cursor-default" : ""}`}
                              data-testid={`quiz-q${qi}-opt${oi}`}
                            >
                              <span className="font-heading font-bold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                            </button>
                          );
                        })}
                      </div>
                      {/* Não mostramos qual opção estava certa nem errada —
                          apenas o resultado final abaixo. */}
                    </li>
                  );
                })}
              </ol>

              <div className="mt-8 flex items-center justify-between gap-3 flex-wrap border-t border-[hsl(var(--border))] pt-6">
                <div>
                  {result ? (
                    <div data-testid="quiz-result-score">
                      <div className={`font-heading font-black text-lg ${result.passed ? "text-[hsl(var(--primary))]" : "text-red-600"}`}>
                        {result.passed ? "Aprovado!" : "Não passou desta vez."} · {result.score}%
                      </div>
                      <div className="text-xs font-normal text-[hsl(var(--muted-foreground))] mt-1">
                        {result.correct} de {result.total} corretas · Tentativa {result.attempts}
                      </div>
                      <div className="mt-3 text-sm text-[hsl(var(--foreground))] max-w-md leading-relaxed" data-testid="quiz-result-message">
                        {(() => {
                          const s = result.score;
                          if (result.passed && s >= 90) return "Você mandou muito bem 💚 Que orgulho ver esse cuidado com o aprendizado — leve isso pra sua família.";
                          if (result.passed) return "Parabéns, você concluiu com carinho 🌱 Cada passo desses fortalece o acolhimento em casa.";
                          if (s >= 60) return "Continue firme, você está muito perto! Respira, revisa o módulo com calma e tenta de novo — a gente acredita em você 🤗";
                          return "Sem pressa — aprender é um caminho, não uma corrida. Revisa o conteúdo no seu ritmo e volta quando quiser 💛";
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-[hsl(var(--muted-foreground))]">Escolha uma opção em cada pergunta.</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {result && !result.passed && (
                    <Button onClick={retake} variant="outline" className="rounded-full" data-testid="quiz-retake-btn">
                      <RotateCcw className="h-4 w-4 mr-1.5" /> Tentar novamente
                    </Button>
                  )}
                  {!result && (
                    <Button onClick={submitQuiz} disabled={submitting || !allAnswered}
                      className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 h-11 px-6"
                      data-testid="quiz-submit-btn">
                      {submitting ? "Corrigindo…" : "Enviar respostas"}
                    </Button>
                  )}
                  {result?.passed && next && (
                    <Button onClick={() => navigate(`/cursos/${slug}/aula/${next.id}`)} className="rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="quiz-next-module-btn">
                      Próximo módulo <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  )}
                  {result?.passed && result?.course_completed && (
                    <Button onClick={() => navigate("/associado/certificados")} className="rounded-full bg-[hsl(var(--secondary))] text-white hover:bg-[hsl(var(--secondary))]/90" data-testid="quiz-cert-btn">
                      <Award className="h-4 w-4 mr-1.5" /> Ver certificado
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <aside>
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="surface p-5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))]">Grade</div>
              <ol className="mt-3 space-y-1">
                {course.modules.map((m) => {
                  const mp = course.enrollment.modules_progress[m.id];
                  const done = !!mp?.passed_at;
                  const active = m.id === moduleId;
                  return (
                    <li key={m.id}>
                      <button onClick={() => navigate(`/cursos/${slug}/aula/${m.id}`)}
                        className={`w-full text-left flex items-start gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${
                          active ? "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--muted))]/40"
                        }`}
                        data-testid={`sidebar-module-${m.id}`}>
                        <div className={`h-6 w-6 rounded-full grid place-items-center shrink-0 text-[10px] font-heading font-black ${
                          done ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))]"
                        }`}>{done ? <CheckCircle2 className="h-3 w-3" /> : m.order}</div>
                        <span className="line-clamp-2">{m.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
            <div className="flex gap-2">
              {prev && <Button onClick={() => navigate(`/cursos/${slug}/aula/${prev.id}`)} variant="outline" className="rounded-full flex-1" data-testid="nav-prev-btn"><ChevronLeft className="h-4 w-4 mr-1" /> Anterior</Button>}
              {next && <Button onClick={() => navigate(`/cursos/${slug}/aula/${next.id}`)} variant="outline" className="rounded-full flex-1" data-testid="nav-next-btn">Próximo <ChevronRight className="h-4 w-4 ml-1" /></Button>}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
